// Public sales page for the No Excuses Dispute Manager SOFTWARE — sold to
// credit-repair BUSINESS OWNERS, never to consumers (no credit-outcome
// promises anywhere on this page).
//
// Order is deliberate: the visitor gives name, business, email and phone
// FIRST; that is saved as a lead (/api/software-leads) before anyone is sent
// to Stripe, so an abandoned checkout is still a lead Key can follow up. Then
// /api/checkout/dispute-manager creates the Stripe Checkout Session. Access
// is granted only by the Stripe webhook after payment clears.
//
// Until Key sets the price, GET /api/checkout/dispute-manager reports
// enabled:false — the page then shows "opening soon" and the same form simply
// joins the early-access list (the lead is still captured, and Key is pinged).
import {
  ArrowRight, Bot, Check, FileSearch, FileText, Inbox, Loader2, Lock, Mail,
  MessageSquare, Receipt, ShieldCheck, Users, Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { SalesVideo, StickyStart } from "../components/SalesKit";
import { SALES_VIDEOS } from "../lib/sales-videos";
import { onSalesPage, pixelEvent, pixelPageView } from "../lib/meta-pixel";
import { Link } from "react-router-dom";
import "../styles/software-landing.css";
import { ApiError, softwareSales, type SoftwarePlan, type SoftwareSalesConfig, type SoftwareTier } from "../lib/api";

const FEATURES = [
  { icon: FileSearch, title: "Credit report import + analysis", body: "Pull reports from MyFreeScoreNow and IdentityIQ with the client's saved login. Every negative item is scored, sorted by bureau, and tied to the FCRA section it can be disputed under." },
  { icon: FileText, title: "Dispute letters, built for you", body: "§611 reinvestigation, §605B identity-theft blocks, late-payment, public-record and §623 furnisher letters are generated from the analysis, signed by the client and bundled per bureau with their ID. Method-of-verification follow-ups are drafted with Attorney Brian." },
  { icon: Inbox, title: "Client intake link", body: "One link does the whole onboarding: identity, documents, e-signed service agreement with the CROA disclosures and 3-day right to cancel, and credit-monitoring enrollment through your own affiliate link." },
  { icon: Users, title: "Branded client portal", body: "Clients log in to upload documents, see their progress, sign letters and message your team — under your company name, not ours." },
  { icon: Bot, title: "Attorney Brian, your AI paralegal", body: "Connect your own Claude account and Brian works inside your company's data: reads analyses, drafts dispute rounds, and prepares CFPB and FTC filings for your review." },
  { icon: Receipt, title: "Bill for completed work", body: "Invoice clients for the work you've completed and let them pay by card online. The workflow is designed around billing after services are performed, not up front." },
  { icon: Mail, title: "Certified mail from the app", body: "Send letters by certified mail through your own Lob or LetterStream account, with tracking on every piece." },
  { icon: MessageSquare, title: "Team, messaging and alerts", body: "Staff logins with permission controls, client messaging, and instant Discord or email alerts when someone signs up, finishes intake or pays." },
];

const INCLUDED = [
    "Credit report import and analysis",
  "Core dispute letters generated from the analysis",
  "Client intake link and branded client portal",
  "E-signed service agreements with CROA disclosures",
  "Attorney Brian AI paralegal (bring your own Claude)",
  "Online invoicing and card payments",
  "Staff logins and permissions",
  "Certified mail integration (your own carrier account)",
];

// Display copy per tier. Amounts are NEVER hard-coded here — they come from
// Stripe through GET /api/checkout/dispute-manager, so the page can't drift
// from what Checkout actually charges.
const PLAN_COPY: Record<SoftwareTier, { who: string; clients: string; highlight?: boolean }> = {
  solo: { who: "Just for yourself", clients: "1 client file" },
  pro: { who: "For a growing business", clients: "Up to 20 clients", highlight: true },
  unlimited: { who: "For established companies", clients: "Unlimited clients" },
};
const TIER_ORDER: SoftwareTier[] = ["solo", "pro", "unlimited"];

const FAQ = [
  { q: "Who is this for?", a: "Credit repair business owners and their staff. It is software to run your company — it does not provide credit repair to consumers and makes no promises about credit outcomes." },
  { q: "How do I get set up after I pay?", a: "Right after checkout we email you a private setup link. You name your company, set your password, and you're in. From there you can invite staff and send your first client intake link." },
  { q: "Does it charge my clients up front?", a: "No. The Credit Repair Organizations Act prohibits charging for credit repair before the work is done, so the software bills clients by invoice after work is performed." },
  { q: "Can I cancel?", a: "Yes, anytime, on your own — no call or email needed. In the app go to Settings → Subscription → Manage subscription and click Cancel. It takes effect at the end of the month you've already paid for, and you won't be charged again." },
  { q: "What counts as a client?", a: "Any client file that isn't closed. Leads and prospects from your sign-up link don't count toward your plan's limit — convert a lead to a client when you're ready to start working their file." },
  { q: "Can I change plans later?", a: "Yes — contact us and we'll move you to the plan that fits as your business grows." },
];

const UTM_KEY = "nx_sw_utm";
// Only the standard campaign keys, each capped, and the whole object kept well
// under the server's 2 KB limit — an oversized tracking link must never block
// a real person from submitting the form.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "src", "ref"];
export function boundedUtm(input: Record<string, unknown> | null): Record<string, string> | null {
  if (!input) return null;
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) {
    const v = input[k];
    if (typeof v === "string" && v) out[k] = v.slice(0, 120);
  }
  // Measure UTF-8 BYTES (what the server counts), not JS characters: 120
  // non-Latin characters can be 360+ bytes.
  const bytes = (o: object) => new TextEncoder().encode(JSON.stringify(o)).length;
  while (Object.keys(out).length && bytes(out) > 1500) delete out[Object.keys(out).pop()!];
  return Object.keys(out).length ? out : null;
}
export function readUtm(): Record<string, string> | null {
  try {
    const p = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    p.forEach((v, k) => { found[k.toLowerCase()] = v; });
    const fresh = boundedUtm(found);
    if (fresh) { sessionStorage.setItem(UTM_KEY, JSON.stringify(fresh)); return fresh; }
    const saved = sessionStorage.getItem(UTM_KEY);
    return saved ? boundedUtm(JSON.parse(saved)) : null;
  } catch { return null; }
}

export function money(cents: number, currency: string) {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
  catch { return `$${(cents / 100).toFixed(2)}`; }
}


/** Where the visitor came from — origin + path only (no query string), and
 *  never longer than the server accepts, so tracking can't block the form. */
export function boundedReferrer(ref: string): string | null {
  if (!ref) return null;
  try {
    const u = new URL(ref);
    const v = `${u.origin}${u.pathname}`;
    return v.length <= 480 ? v : v.slice(0, 480);
  } catch { return null; }
}

/** Plain-English message for each error the sales endpoints can return. */
export function friendlyError(code: string | null, e: any): string {
  switch (code) {
    case "rate_limited": return "Too many attempts from this connection. Please wait a few minutes and try again.";
    case "bad_email": return "Please enter a valid email address.";
    case "bad_tier": return "Please choose a plan above.";
    case "lead_required": return "We couldn't save your details. Please submit the form again.";
    case "checkout_unavailable": return "Secure checkout isn't available right now. Please try again in a few minutes.";
    case "account_exists": return "This email already has a Dispute Manager login. Sign in, or use a different email for a new company.";
    case "acknowledgement_required": return "Please tick the box to confirm you understand this is do-it-yourself software.";
    case "invalid_body":
    case "validation_error": {
      const fields: string[] = Array.isArray(e?.body?.fields) ? e.body.fields : [];
      const names: Record<string, string> = { full_name: "full name", business_name: "business name", email: "email", phone: "phone number" };
      const list = fields.map((f) => names[f]).filter(Boolean).join(", ");
      return list ? `Please check your ${list}.` : "Please check the form and try again.";
    }
    default: return "Something went wrong. Please try again, or email us.";
  }
}

export default function SoftwarePricingPage() {
  const [cfg, setCfg] = useState<SoftwareSalesConfig | null>(null);
  const [form, setForm] = useState({ full_name: "", business_name: "", email: "", phone: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const initialTier = useMemo<SoftwareTier>(() => {
    const t = new URLSearchParams(window.location.search).get("plan");
    return t === "solo" || t === "pro" || t === "unlimited" ? t : "pro";
  }, []);
  const [tier, setTier] = useState<SoftwareTier>(initialTier);
  const utm = useMemo(readUtm, []);
  const cancelled = useMemo(() => new URLSearchParams(window.location.search).get("checkout") === "cancelled", []);

  useEffect(() => {
    document.title = "No Excuses Dispute Manager — Credit Repair Software";
    pixelPageView();
    softwareSales.config().then(setCfg).catch(() => setCfg({ ok: true, enabled: false, plans: [] }));
  }, []);

  const plans: SoftwarePlan[] = useMemo(() => {
    const list = cfg?.plans || [];
    return TIER_ORDER.map((t) => list.find((p) => p.tier === t)).filter(Boolean) as SoftwarePlan[];
  }, [cfg]);
  const open = !!(cfg?.enabled && plans.length === 3);
  const chosen = plans.find((p) => p.tier === tier) || null;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const email = form.email.trim();
    if (form.full_name.trim().length < 2) return setErr("Please enter your full name.");
    if (form.business_name.trim().length < 2) return setErr("Please enter your business name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setErr("Please enter a valid email address.");
    if (form.phone.replace(/\D/g, "").length < 10) return setErr("Please enter a valid phone number.");
    setBusy(true);
    try {
      // 1) Capture the lead FIRST — even if they never finish checkout. Only a
      //    confirmed save (captured:true) counts; the server returns
      //    captured:false when it could not store the lead, and we must not
      //    tell the visitor they're on the list — or send them to Stripe — then.
      const lead = await softwareSales.captureLead({
        full_name: form.full_name.trim(), business_name: form.business_name.trim(), email,
        phone: form.phone.trim(), website: form.website, tier, source: "dispute_manager_pricing",
        utm, referrer: boundedReferrer(document.referrer),
      });
      if (!lead.captured) {
        setErr("We couldn't save your details just now. Please try again in a moment.");
        return;
      }
      if (!open) { setJoined(true); return; }
      // 2) Then hand them to Stripe Checkout.
      const r = await softwareSales.startCheckout(email, tier);
      if (chosen) pixelEvent("InitiateCheckout", { value: chosen.amount_cents / 100, currency: chosen.currency.toUpperCase(), content_name: `Dispute Manager ${chosen.label}` });
      window.location.assign(r.url);
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.error : null;
      if (code === "sales_not_open") setJoined(true);
      else setErr(friendlyError(code, e));
    } finally { setBusy(false); }
  }

  return (
    <div className="sw">
      <header className="sw-nav">
        <div className="sw-shell sw-nav-inner">
          <a href="#top" className="sw-logo"><img src="/brand-logo.png" alt="" />NO EXCUSES <em>DISPUTE MANAGER</em></a>
          <nav className="sw-nav-links">
            <a className="sw-btn sw-btn-ghost" href="https://app.noexcusesdisputes.com/login">Sign in</a>
            <a className="sw-btn sw-btn-primary" href="#get-started">Get started</a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="sw-hero">
          <div className="sw-shell sw-hero-split">
            <div>
            <h1 className="sw-h1">Run your credit repair business <span>without the busywork</span></h1>
            <p className="sw-lede">
              Import reports, find every disputable item, generate the letters, get them signed and mailed,
              and bill for completed work — one system for your whole operation, built by a credit repair
              company for credit repair companies.
            </p>
            <div className="sw-cta-row">
              <a className="sw-btn sw-btn-primary" href="#get-started">{open ? "Choose your plan" : "Get early access"} <ArrowRight size={18} aria-hidden="true" /></a>
              <a className="sw-btn sw-btn-ghost" href="#features">See what's inside</a>
            </div>
            </div>
            <SalesVideo src={SALES_VIDEOS.business} title="Watch the full walkthrough" />
          </div>
        </section>
        <hr className="sw-rule" />

        <section className="sw-section" id="features">
          <div className="sw-shell">
            <h2 className="sw-h2">Everything your team does, in one place</h2>
            <p className="sw-sub">From the first intake link to the final dispute round — no spreadsheets, no copy-pasting letters, no chasing clients for documents.</p>
            <div className="sw-grid">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <div className="sw-card" key={title}>
                  <div className="sw-icon"><Icon size={20} aria-hidden="true" /></div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sw-section" style={{ paddingTop: 0 }}>
          <div className="sw-shell">
            <h2 className="sw-h2">Up and running the same day</h2>
            <p className="sw-sub">No sales call, no onboarding fee.</p>
            <div className="sw-steps">
              <div className="sw-step"><h3>Subscribe</h3><p>Pay securely through Stripe. We never see your card.</p></div>
              <div className="sw-step"><h3>Set up your company</h3><p>Your private setup link arrives by email. Name your company and set your password.</p></div>
              <div className="sw-step"><h3>Invite clients and staff</h3><p>Send your intake link, add your team, and start your first dispute round.</p></div>
            </div>
          </div>
        </section>

        <section className="sw-section" id="get-started" style={{ paddingTop: 0 }}>
          <div className="sw-shell sw-buy">
            <div>
              <span className="sw-eyebrow">Monthly plans · month to month</span>
              {cfg === null ? (
                <div className="sw-soon"><Loader2 size={26} className="spin" aria-label="Loading plans" /></div>
              ) : !open ? (
                <div className="sw-price-card" style={{ marginTop: 14 }}>
                  <div className="sw-soon">Pricing opening soon</div>
                  <p style={{ color: "var(--ne-muted)", margin: "6px 0 0", fontSize: 14 }}>
                    Join the early-access list and we'll reach out personally as soon as subscriptions open.
                  </p>
                </div>
              ) : (
                <div className="sw-plans" role="radiogroup" aria-label="Choose a plan">
                  {plans.map((p) => {
                    const copy = PLAN_COPY[p.tier];
                    const active = p.tier === tier;
                    return (
                      <button type="button" key={p.tier} role="radio" aria-checked={active}
                        className={`sw-plan${active ? " is-active" : ""}${copy.highlight ? " is-popular" : ""}`}
                        onClick={() => setTier(p.tier)}>
                        {copy.highlight && <span className="sw-plan-badge">Most popular</span>}
                        <span className="sw-plan-name">{p.label}</span>
                        <span className="sw-plan-who">{copy.who}</span>
                        <span className="sw-price">{money(p.amount_cents, p.currency)}<small>/{p.interval}</small></span>
                        <span className="sw-plan-clients"><Users size={14} aria-hidden="true" /> {copy.clients}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="sw-price-card" style={{ marginTop: 14 }}>
                <strong style={{ fontSize: 15 }}>Every plan includes</strong>
                <ul className="sw-list">
                  {INCLUDED.map((i) => <li key={i}><Check size={16} aria-hidden="true" />{i}</li>)}
                </ul>
              </div>
            </div>

            <form className="sw-form" id="order-form" onSubmit={submit} noValidate>
              {joined ? (
                <div className="sw-ok" role="status">
                  <strong>You're on the list.</strong><br />
                  Thanks, {form.full_name.trim().split(/\s+/)[0] || "there"} — we'll contact you at {form.email.trim()} as soon as subscriptions open.
                </div>
              ) : (
                <>
                  <h3>{open && chosen ? `Start ${chosen.label} — ${money(chosen.amount_cents, chosen.currency)}/${chosen.interval}` : "Get early access"}</h3>
                  <p className="sw-note">
                    {open ? "Tell us who you are, then you'll finish securely on Stripe." : "Tell us who you are and we'll reach out as soon as it opens."}
                  </p>
                  {cancelled && !err && <p className="sw-error">Checkout was cancelled — nothing was charged. You can try again below.</p>}
                  {err && <p className="sw-error" role="alert">{err}</p>}
                  <div className="sw-field"><label htmlFor="sw-name">Full name</label>
                    <input id="sw-name" autoComplete="name" value={form.full_name} onChange={set("full_name")} maxLength={120} required /></div>
                  <div className="sw-field"><label htmlFor="sw-biz">Business name</label>
                    <input id="sw-biz" autoComplete="organization" value={form.business_name} onChange={set("business_name")} maxLength={120} required /></div>
                  <div className="sw-row2">
                    <div className="sw-field"><label htmlFor="sw-email">Email</label>
                      <input id="sw-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} maxLength={254} required /></div>
                    <div className="sw-field"><label htmlFor="sw-phone">Phone</label>
                      <input id="sw-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} maxLength={25} required /></div>
                  </div>
                  {/* Honeypot: invisible to people, filled by bots. */}
                  <div className="sw-hp" aria-hidden="true">
                    <label htmlFor="sw-website">Website</label>
                    <input id="sw-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  </div>
                  <button className="sw-btn sw-btn-primary" type="submit" disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
                    {busy ? <Loader2 size={18} className="spin" aria-hidden="true" /> : open ? <Lock size={16} aria-hidden="true" /> : <Zap size={16} aria-hidden="true" />}
                    {busy ? "One moment…" : open ? "Continue to secure checkout" : "Join the early-access list"}
                  </button>
                  <p className="sw-fine">
                    <ShieldCheck size={13} aria-hidden="true" style={{ verticalAlign: -2 }} /> We'll use your details only to contact you about the Dispute Manager.
                    Software for credit repair businesses; it is not legal advice, and you remain responsible for complying with the CROA and the laws of your state.
                  </p>
                </>
              )}
            </form>
          </div>
        </section>

        <section className="sw-section" style={{ paddingTop: 0 }}>
          <div className="sw-shell">
            <h2 className="sw-h2">Questions</h2>
            <div className="sw-faq">
              {FAQ.map(({ q, a }) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}
            </div>
          </div>
        </section>
      </main>

      {open && chosen && (
        <StickyStart targetId="order-form" label={`${chosen.label} — ${money(chosen.amount_cents, chosen.currency)}/mo`} sub="Month to month · cancel anytime" />
      )}
      <footer className="sw-footer">
        <div className="sw-shell">
          © {new Date().getFullYear()} Low Key Global Financial LLC · No Excuses Dispute Manager ·{" "}
          <a href="https://app.noexcusesdisputes.com/login">Sign in</a>
        </div>
      </footer>
    </div>
  );
}

// Shown after Stripe Checkout succeeds. Access is granted by the webhook, not
// by this page — it only tells the buyer where their setup link went.
export function SoftwareWelcomePage() {
  useEffect(() => { document.title = "Welcome — No Excuses Dispute Manager"; }, []);
  const consumer = useMemo(() => new URLSearchParams(window.location.search).get("offer") === "consumer", []);
  useEffect(() => {
    pixelPageView();
    // Purchase fires only when Stripe confirms this session is a PAID Dispute
    // Manager subscription — never on a bare /welcome hit — and once per session.
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let fired = false;
    try { fired = sessionStorage.getItem(`nx_px_purchase_${sid}`) === "1"; } catch { /* storage blocked */ }
    if (fired) return;
    const ctl = new AbortController();
    fetch(`/api/checkout/confirm?session_id=${encodeURIComponent(sid)}`, { credentials: "omit", signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        // Never fire once the visitor has left this page (unmount aborts; the
        // route check is the backstop).
        if (!d?.paid || ctl.signal.aborted || !onSalesPage()) return;
        pixelEvent("Purchase", { value: d.value_cents / 100, currency: String(d.currency).toUpperCase(),
          content_name: d.offer === "consumer" ? "Dispute Manager (consumer)" : "Dispute Manager (business)" }, sid);
        try { sessionStorage.setItem(`nx_px_purchase_${sid}`, "1"); } catch { /* storage blocked */ }
      })
      .catch(() => { /* aborted or offline — tracking must never break the page */ });
    return () => ctl.abort();
  }, []);
  return (
    <div className="sw">
      <div className="sw-welcome">
        <div className="sw-icon" style={{ margin: "0 auto", width: 54, height: 54 }}><Check size={28} aria-hidden="true" /></div>
        <h1 className="sw-h2" style={{ marginTop: 18 }}>Payment received — welcome aboard</h1>
        <p className="sw-sub" style={{ margin: "0 auto 22px" }}>
          Check your email for your private setup link. Open it to name your {consumer ? "workspace" : "company"} and set your password.
          It can take a minute or two to arrive — check spam if you don't see it.
        </p>
        <p className="sw-fine">The link expires in 30 days. Questions? Just reply to the welcome email.</p>
        {consumer && (
          <p className="sw-fine">Do-it-yourself dispute software: you run your own disputes; we don't do them for you. Results vary and no outcome is guaranteed.</p>
        )}
        <p style={{ marginTop: 22 }}><a className="sw-btn sw-btn-ghost" href={consumer ? "/login" : "/pricing"}>{consumer ? "Go to sign in" : "Back to the Dispute Manager"}</a></p>
      </div>
    </div>
  );
}
